import React, {
  useRef,
  useState,
  useEffect
} from "react";

import {
  Modal,
  Button
} from "react-bootstrap";

import BillingService from "../../../services/BillingService";
import Swal from "sweetalert2";

const styles = {
  container: {
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    margin: "20px"
  },

  headingBox: {
    textAlign: "center",
    fontSize: "15px",
    fontWeight: "bold",
    marginBottom: "10px"
  },

  borderBox: {
    border: "1px solid #000",
    padding: "10px",
    marginBottom: "10px"
  },

  sectionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px"
  },

  section: {
    border: "1px solid #000",
    padding: "6px",
    flex: 1,
    minWidth: "300px"
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    textTransform: "uppercase"
  },

  thTd: {
    border: "1px solid #000",
    padding: "4px",
    verticalAlign: "top",
    textTransform: "uppercase"
  },

  thead: {
    backgroundColor: "#f0f0f0",
    textTransform: "uppercase"
  },

  signature: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "40px",
    textTransform: "uppercase"
  },

  underline: {
    textDecoration: "underline"
  },

  maskedValue: {
    letterSpacing: "3px",
    fontWeight: "bold",
    whiteSpace: "nowrap"
  }
};

const PoPrintModal = ({
  show,
  onHide,
  item
}) => {
  const printRef = useRef();

  const [billingAddress, setBillingAddress] =
    useState(null);

  const [supplier, setSupplier] =
    useState(null);
// ==========================================
// LOGGED-IN USER
// ==========================================
const loggedInUser = JSON.parse(
  localStorage.getItem("userDetails") || "{}"
);

const userRole = String(
  loggedInUser?.data?.user?.role || ""
)
  .trim()
  .toLowerCase();

// ==========================================
// PRINT ALLOWED ROLES
// Only PO Maker, PO Checker and Director
// can print the Repair Order.
// ==========================================
const canPrintRepairPO = [
  "pomaker",
  "pochecker",
  "director"
].includes(userRole);

// ==========================================
// COMMERCIAL VALUE RESTRICTED ROLES
// These roles can view the PDF,
// but commercial values remain masked.
// ==========================================
const commercialRestrictedRoles = [
  "storekeeper",
  "admin",
  "superadmin"
];

const hideCommercialValues =
  commercialRestrictedRoles.includes(
    userRole
  );

// Credit-card-style hidden commercial value
const maskedCommercialValue = (
  <span
    className="commercial-mask"
    style={styles.maskedValue}
  >
    ••••••••
  </span>
);

// Credit-card-style hidden percentage
const maskedPercentage = (
  <span
    className="commercial-mask"
    style={styles.maskedValue}
  >
    ••••
  </span>
);


  // ==========================================
  // NUMBER TO WORDS
  // ==========================================
  const numberToWordsWithPaise = (amount) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen"
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety"
    ];

    const numToWords = (number) => {
      if (number < 20) {
        return ones[number];
      }

      if (number < 100) {
        return `${
          tens[Math.floor(number / 10)]
        } ${
          ones[number % 10]
        }`.trim();
      }

      if (number < 1000) {
        return `${
          ones[Math.floor(number / 100)]
        } Hundred ${
          numToWords(number % 100)
        }`.trim();
      }

      if (number < 100000) {
        return `${
          numToWords(
            Math.floor(number / 1000)
          )
        } Thousand ${
          numToWords(number % 1000)
        }`.trim();
      }

      if (number < 10000000) {
        return `${
          numToWords(
            Math.floor(number / 100000)
          )
        } Lakh ${
          numToWords(number % 100000)
        }`.trim();
      }

      return `${
        numToWords(
          Math.floor(number / 10000000)
        )
      } Crore ${
        numToWords(number % 10000000)
      }`.trim();
    };

    const [rupees, paise] = Number(
      amount || 0
    )
      .toFixed(2)
      .split(".")
      .map(Number);

    const rupeePart =
      rupees === 0
        ? ""
        : `${numToWords(rupees)} Rupees`;

    const paisePart =
      paise === 0
        ? ""
        : `${numToWords(paise)} Paise`;

    const combined = [
      rupeePart,
      paisePart
    ]
      .filter(Boolean)
      .join(" and ");

    return combined
      ? `${combined} Only`
      : "Zero Rupees Only";
  };

  // ==========================================
  // BILLING ADDRESS
  // ==========================================
  const billingState =
    item?.repairOrder?.store
      ?.address?.state;

  useEffect(() => {
    const fetchBillingAddress =
      async () => {
        try {
          if (!billingState) {
            return;
          }

          const res =
            await BillingService
              .activeSuppliersState(
                billingState
              );

          if (res?.data?.length > 0) {
            setBillingAddress(
              res.data[0]
            );
          }
        } catch (error) {
          console.error(
            "Error fetching billing address:",
            error
          );

          Swal.fire({
            icon: "error",
            title:
              "Failed to fetch billing address",
            text:
              error?.response?.data
                ?.message ||
              "Something went wrong"
          });
        }
      };

    if (show) {
      fetchBillingAddress();
    }
  }, [
    show,
    billingState
  ]);

  // ==========================================
  // SUPPLIER
  // ==========================================
  useEffect(() => {
    if (
      item?.items?.length > 0 &&
      item?.items?.[0]?.supplier
    ) {
      setSupplier(
        item.items[0].supplier
      );
    } else if (item?.supplier) {
      setSupplier(item.supplier);
    }
  }, [item]);

  // ==========================================
// PRINT
// ==========================================
const handlePrint = () => {
  // Extra protection:
  // prevent unauthorized roles from printing
  // even if the function is called manually.
  if (!canPrintRepairPO) {
    Swal.fire(
      "Access Denied",
      "Only PO Maker, PO Checker and Director can print the Repair Order.",
      "warning"
    );

    return;
  }

  if (!printRef.current) {
    return;
  }

  const printWindow = window.open(
    "",
    "",
    "height=900,width=800"
  );

  if (!printWindow) {
    Swal.fire(
      "Print Blocked",
      "Please allow pop-ups to print the Repair Order.",
      "warning"
    );

    return;
  }

  printWindow.document.write(
    "<html><head><title>Print Repair Order</title>"
  );

  printWindow.document.write(`
    <style>
      body {
        font-family: Arial, sans-serif;
        font-size: 13px;
        margin: 20px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        text-transform: uppercase;
      }

      th,
      td {
        border: 1px solid #000;
        padding: 6px;
        vertical-align: top;
        text-transform: uppercase;
      }

      thead th {
        background-color: #f0f0f0;
        text-transform: uppercase;
      }

      .signature,
      .section,
      .borderBox {
        text-transform: uppercase;
      }

      .underline {
        text-decoration: underline;
      }

      /* Keep masked values hidden in print */
      .commercial-mask {
        letter-spacing: 3px;
        font-weight: bold;
        white-space: nowrap;
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      div,
      span {
        text-transform: uppercase;
      }

      ol {
        list-style-type: decimal;
        margin: 0;
        padding-left: 18px;
      }

      ol li {
        list-style-position: inside;
        text-transform: none;
      }
    </style>
  `);

  printWindow.document.write(
    "</head><body>"
  );

  printWindow.document.write(
    printRef.current.innerHTML
  );

  printWindow.document.write(
    "</body></html>"
  );

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
};

  // ==========================================
  // FORMAT CURRENCY
  // ==========================================
  const formatCurrency = (value) => {
    const number = Number(value);

    return Number.isNaN(number)
      ? "₹0.00"
      : number.toLocaleString(
          "en-IN",
          {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2
          }
        );
  };

  const isFOC =
    item?.poType === "FOC PO";

  const hasIGST =
    item?.items?.some(
      (row) =>
        Number(row?.igst || 0) > 0
    );

  let totalQty = 0;
  let totalAmount = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  let totalInclGst = 0;

  // ==========================================
  // ITEM ROW
  // ==========================================
  const getItemRow = (
    row,
    index
  ) => {
    const qty =
      Number(row?.approveQty) || 0;

    const rate =
      Number(row?.rate) || 0;

    const cgst =
      Number(row?.cgst) || 0;

    const sgst =
      Number(row?.sgst) || 0;

    const igst =
      Number(row?.igst) || 0;

    const baseAmount =
      qty * rate;

    const gstAmount =
      baseAmount *
      (
        (
          cgst +
          sgst +
          igst
        ) /
        100
      );

    const amountInclGST =
      baseAmount + gstAmount;

    totalQty += qty;

    if (!isFOC) {
      totalAmount += baseAmount;

      totalCGST +=
        (cgst / 100) *
        baseAmount;

      totalSGST +=
        (sgst / 100) *
        baseAmount;

      totalIGST +=
        (igst / 100) *
        baseAmount;

      totalInclGst +=
        amountInclGST;
    }

    return (
      <tr key={row?._id || index}>
        <td style={styles.thTd}>
          {index + 1}
        </td>

        <td style={styles.thTd}>
          {row?.description || "-"}
        </td>

        <td style={styles.thTd}>
          {row?.unit || "-"}
        </td>

        <td style={styles.thTd}>
          {qty}
        </td>

        {/* RATE */}
        <td style={styles.thTd}>
          {hideCommercialValues
            ? maskedCommercialValue
            : formatCurrency(rate)}
        </td>

        {/* BASE AMOUNT */}
        {!isFOC && (
          <td style={styles.thTd}>
            {hideCommercialValues
              ? maskedCommercialValue
              : formatCurrency(
                  baseAmount
                )}
          </td>
        )}

        {/* CGST PERCENTAGE */}
        {!isFOC && (
          <td style={styles.thTd}>
            {hideCommercialValues
              ? maskedPercentage
              : cgst}
          </td>
        )}

        {/* SGST PERCENTAGE */}
        {!isFOC && (
          <td style={styles.thTd}>
            {hideCommercialValues
              ? maskedPercentage
              : sgst}
          </td>
        )}

        {/* IGST PERCENTAGE */}
        {hasIGST && !isFOC && (
          <td style={styles.thTd}>
            {hideCommercialValues
              ? maskedPercentage
              : igst}
          </td>
        )}

        {/* AMOUNT INCLUDING GST */}
        {!isFOC && (
          <td style={styles.thTd}>
            {hideCommercialValues
              ? maskedCommercialValue
              : formatCurrency(
                  amountInclGST
                )}
          </td>
        )}
      </tr>
    );
  };

  if (!item) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Repair Order
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          ref={printRef}
          style={styles.container}
        >
          {/* ==================================
              COMPANY INFORMATION
          ================================== */}
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              marginBottom: "20px"
            }}
          >
            <img
              src="https://indigoinfra.in/img/product/product-1739471679615.jpeg"
              alt="Indigo Logo"
              width="200"
              height="100"
              style={{
                borderRadius: "2px"
              }}
            />

            <div
              style={{
                textAlign: "right"
              }}
            >
              <h2 style={{ margin: 0 }}>
                Indigo Infraprojects
                Pvt. Ltd.
              </h2>

              <br />

              <span className="text-uppercase">
                3rd Floor, Plot #12/B,
                Pocket 3, Sec-17,
                Dwarka, New
                Delhi-110075
                <br />

                Ph: +91-11-45720141,
                44458664
                <br />

                info@indigoinfraprojects.com
                <br />
              </span>

              <strong>
                CIN:
                U70102DL2012PTC232657
              </strong>
            </div>
          </div>

          {/* ==================================
              HEADING
          ================================== */}
          <div style={styles.headingBox}>
            {item?.status ===
            "Confirmed-Generated"
              ? "REPAIR ORDER (DIGITAL COPY)"
              : item?.status ===
                  "Order-Received"
                ? "REPAIR ORDER (ORDER RECEIVED)"
                : item?.status ===
                    "Partially-Received"
                  ? "REPAIR ORDER (PARTIALLY RECEIVED)"
                  : item?.status ===
                      "Completed"
                    ? "REPAIR ORDER (COMPLETED)"
                    : item?.status ===
                        "Bill-Generated"
                      ? "REPAIR ORDER (UNDER BILLING)"
                      : "REPAIR ORDER (UNDER APPROVAL)"}
          </div>

          {/* ==================================
              ORDER AND SUPPLIER DETAILS
          ================================== */}
          <div style={styles.borderBox}>
            <div style={styles.sectionRow}>
              <div style={styles.section}>
                <p className="text-uppercase">
                  <strong>
                    REPAIR ORDER NO
                  </strong>{" "}
                  -{" "}
                  {new Date(
                    item?.createdAt
                  ).getFullYear()}
                  /
                  {String(
                    new Date(
                      item?.createdAt
                    ).getMonth() + 1
                  ).padStart(2, "0")}
                  /
                  {item?.repairOrder?.store
                    ?.name || ""}
                  /
                  {item?.poNumber}

                  <br />

                  <strong>
                    Order Date:
                  </strong>{" "}
                  {new Date(
                    item?.createdAt
                  ).toLocaleDateString(
                    "en-IN"
                  )}

                  <br />

                  <strong>
                    Request No:
                  </strong>{" "}
                  {
                    item?.repairOrder
                      ?.repairOrderNo
                  }
                </p>
              </div>

              <div style={styles.section}>
                <p className="text-uppercase">
                  <strong>
                    Billing Address
                  </strong>

                  <br />

                  Indigo Infraprojects
                  Pvt. Ltd.

                  <br />

                  {billingAddress?.address}

                  <br />

                  <strong>
                    GSTIN:
                  </strong>

                  {billingAddress?.gstNumber}
                </p>
              </div>
            </div>

            <div
              style={{
                ...styles.sectionRow,
                marginTop: "5px"
              }}
            >
              <div style={styles.section}>
                <p className="text-uppercase">
                  <strong>
                    Supplier Details
                  </strong>

                  <br />

                  {item?.supplier?.name}

                  <br />

                  {
                    item?.supplier?.address
                      ?.street
                  }
                  ,{" "}
                  {
                    item?.supplier?.address
                      ?.city
                  }
                  ,{" "}
                  {
                    item?.supplier?.address
                      ?.state
                  }{" "}
                  -{" "}
                  {
                    item?.supplier?.address
                      ?.pincode
                  }
                  ,{" "}
                  {
                    item?.supplier?.address
                      ?.country
                  }

                  <br />

                  <strong>
                    Kind Attn:
                  </strong>{" "}
                  {
                    item?.supplier
                      ?.contactPerson
                  }

                  <br />

                  <strong>
                    Phone:
                  </strong>{" "}
                  {Array.isArray(
                    item?.supplier?.mobileNo
                  )
                    ? item.supplier.mobileNo.join(
                        ", "
                      )
                    : item?.supplier
                        ?.mobileNo}

                  <br />

                  <strong>
                    GST:
                  </strong>{" "}
                  {item?.supplier?.GSTNo}

                  <br />

                  <strong>
                    Email:
                  </strong>{" "}
                  {Array.isArray(
                    item?.supplier?.email
                  )
                    ? item.supplier.email.join(
                        ", "
                      )
                    : item?.supplier?.email}
                </p>
              </div>

              <div style={styles.section}>
                <p className="text-uppercase">
                  <strong>
                    Shipping Address
                  </strong>

                  <br />

                  {
                    item?.repairOrder?.store
                      ?.name
                  }
                  ,{" "}
                  {
                    item?.repairOrder?.store
                      ?.address?.street
                  }

                  <br />

                  {
                    item?.repairOrder?.store
                      ?.address?.city
                  }
                  ,{" "}
                  {
                    item?.repairOrder?.store
                      ?.address?.state
                  }{" "}
                  -{" "}
                  {
                    item?.repairOrder?.store
                      ?.address?.pincode
                  }

                  <br />

                  {
                    item?.repairOrder?.store
                      ?.address?.country
                  }

                  <br />

                  <strong>
                    Contact Person:
                  </strong>{" "}
                  {
                    item?.repairOrder?.user
                      ?.name
                  }

                  <br />

                  <strong>
                    Phone:
                  </strong>{" "}
                  {
                    item?.repairOrder?.user
                      ?.mobileNo
                  }
                </p>
              </div>
            </div>
          </div>

          {/* ==================================
              SUBJECT
          ================================== */}
          <div
            style={{
              marginBottom: "20px",
              textTransform: "uppercase"
            }}
          >
            <strong>
              Sub: {item?.subject}
            </strong>

            <br />

            <p style={{ marginTop: "10px" }}>
              Ref: By Quotation On dtd{" "}
              {item?.createdAt &&
                new Date(
                  item.createdAt
                ).toLocaleDateString(
                  "en-GB"
                )}

              <br />

              Dear Sir,

              <br />

              {item?.reference}
            </p>
          </div>

          {/* ==================================
              ITEM TABLE
          ================================== */}
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.thTd}>
                  S. No.
                </th>

                <th style={styles.thTd}>
                  Description
                </th>

                <th style={styles.thTd}>
                  Unit
                </th>

                <th style={styles.thTd}>
                  Qty.
                </th>

                <th style={styles.thTd}>
                  Rate
                </th>

                {!isFOC && (
                  <th style={styles.thTd}>
                    Amount
                  </th>
                )}

                {!isFOC && (
                  <th style={styles.thTd}>
                    CGST (%)
                  </th>
                )}

                {!isFOC && (
                  <th style={styles.thTd}>
                    SGST (%)
                  </th>
                )}

                {hasIGST && !isFOC && (
                  <th style={styles.thTd}>
                    IGST (%)
                  </th>
                )}

                {!isFOC && (
                  <th style={styles.thTd}>
                    Amount incl. GST
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {item?.items?.map(
                getItemRow
              )}

              {/* TOTAL QUANTITY */}
              <tr>
                <td
                  colSpan="3"
                  style={styles.thTd}
                >
                  <strong>
                    Total Qty
                  </strong>
                </td>

                <td style={styles.thTd}>
                  <strong>
                    {totalQty}
                  </strong>
                </td>

                {!isFOC && (
                  <>
                    <td style={styles.thTd}>
                    </td>

                    <td style={styles.thTd}>
                    </td>

                    <td style={styles.thTd}>
                    </td>

                    <td style={styles.thTd}>
                    </td>

                    {hasIGST && (
                      <td style={styles.thTd}>
                      </td>
                    )}

                    <td style={styles.thTd}>
                    </td>
                  </>
                )}
              </tr>

              {!isFOC && (
                <>
                  {/* TOTAL AMOUNT */}
                  <tr>
                    <td
                      colSpan={
                        hasIGST ? 9 : 8
                      }
                      style={styles.thTd}
                    >
                      <strong>
                        Total Amount
                      </strong>
                    </td>

                    <td style={styles.thTd}>
                      {hideCommercialValues
                        ? maskedCommercialValue
                        : formatCurrency(
                            totalAmount
                          )}
                    </td>
                  </tr>

                  {/* CGST TOTAL */}
                  <tr>
                    <td
                      colSpan={
                        hasIGST ? 9 : 8
                      }
                      style={styles.thTd}
                    >
                      CGST Total
                    </td>

                    <td style={styles.thTd}>
                      {hideCommercialValues
                        ? maskedCommercialValue
                        : formatCurrency(
                            totalCGST
                          )}
                    </td>
                  </tr>

                  {/* SGST TOTAL */}
                  <tr>
                    <td
                      colSpan={
                        hasIGST ? 9 : 8
                      }
                      style={styles.thTd}
                    >
                      SGST Total
                    </td>

                    <td style={styles.thTd}>
                      {hideCommercialValues
                        ? maskedCommercialValue
                        : formatCurrency(
                            totalSGST
                          )}
                    </td>
                  </tr>

                  {/* IGST TOTAL */}
                  {hasIGST && (
                    <tr>
                      <td
                        colSpan="9"
                        style={styles.thTd}
                      >
                        IGST Total
                      </td>

                      <td style={styles.thTd}>
                        {hideCommercialValues
                          ? maskedCommercialValue
                          : formatCurrency(
                              totalIGST
                            )}
                      </td>
                    </tr>
                  )}

                  {/* GRAND TOTAL */}
                  <tr>
                    <td
                      colSpan={
                        hasIGST ? 9 : 8
                      }
                      style={styles.thTd}
                    >
                      <strong>
                        Grand Total
                        (incl. taxes &
                        Rounded Off)
                      </strong>
                    </td>

                    <td style={styles.thTd}>
                      <strong>
                        {hideCommercialValues
                          ? maskedCommercialValue
                          : Math.round(
                              totalInclGst
                            ).toLocaleString(
                              "en-IN"
                            )}
                      </strong>
                    </td>
                  </tr>

                  {/* GRAND TOTAL IN WORDS */}
                  <tr>
                    <td
                      colSpan={
                        hasIGST ? 10 : 9
                      }
                      style={styles.thTd}
                    >
                      <strong>
                        Grand Total
                        (in words):
                      </strong>{" "}
                      {hideCommercialValues
                        ? maskedCommercialValue
                        : numberToWordsWithPaise(
                            Math.round(
                              totalInclGst
                            )
                          )}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          {/* ==================================
              TERMS AND CONDITIONS
          ================================== */}
          {item?.termCondition?.length >
            0 && (
            <div
              style={{
                marginTop: "20px"
              }}
            >
              <p>
                <strong>
                  Terms & Conditions:
                </strong>
              </p>

              <ol>
                {item.termCondition.map(
                  (term, index) => {
                    const cleanTerm =
                      term.replace(
                        /^\d+\.\s*/,
                        ""
                      );

                    return (
                      <li key={index}>
                        {cleanTerm}
                      </li>
                    );
                  }
                )}
              </ol>
            </div>
          )}

          {/* ==================================
              SIGNATURES
          ================================== */}
          <div style={{ marginTop: "30px" }}>
            <div
              style={{
                marginBottom: "15px",
                textTransform:
                  "uppercase"
              }}
            >
              <strong>
                Please sign and return
                back a copy of this
                Repair Order as an
                Acceptance.
              </strong>
            </div>

            <div style={styles.signature}>
              <div>
                <p>
                  <br />

                  For and on behalf of

                  <br />

                  <strong>
                    Indigo Infraprojects
                    Pvt. Ltd.
                  </strong>
                </p>

                <div
                  style={{
                    width: "150px",
                    height: "100px",
                    margin: "10px 0px"
                  }}
                />

                <span>
                  Authorised Signature
                </span>
              </div>

              <div
                style={{
                  textAlign: "right"
                }}
              >
                <p>
                  Received and Accepted By

                  <br />

                  For and on Behalf of

                  <br />

                  <strong>
                    {supplier?.name ||
                      item?.supplier?.name}
                  </strong>
                </p>

                <div
                  style={{
                    width: "150px",
                    height: "100px",
                    margin: "10px 0px",
                    marginLeft: "auto"
                  }}
                />

                <span>
                  Authorised Signature
                </span>
              </div>
            </div>
          </div>

          {/* ==================================
              CREATED BY
          ================================== */}
          <div
            style={{
              marginTop: "20px",
              fontWeight: "bold",
              fontSize: "10px"
            }}
          >
            CREATED BY:{" "}
            {item?.createdBy?.name ||
              "N/A"}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
  <Button
    variant="secondary"
    onClick={onHide}
  >
    Close
  </Button>

  {canPrintRepairPO && (
    <Button
      variant="primary"
      onClick={handlePrint}
    >
      Print PO
    </Button>
  )}
</Modal.Footer>
    </Modal>
  );
};

export default PoPrintModal;